/**
 * OrgChartPage — el organigrama de la empresa.
 *
 * ⚠️ El principio que esta pantalla no puede romper: **tiene que ser útil el día 1, en
 * cualquier tenant, con los datos que haya — incluidos ninguno.** De ahí tres cosas que
 * parecen detalles y son el diseño:
 *
 *  - Se dibuja un **bosque**, no un árbol: varias raíces es lo normal (dos gerencias, una
 *    persona part-time), y un tenant recién migrado puede tener 200. Nunca es un error.
 *  - Ningún dato es obligatorio. Sin jefe, sin cargo, sin área, sin contrato vigente: la
 *    ausencia se muestra como lo que es y jamás deja la pantalla en blanco.
 *  - El panel de completitud **empuja a completar en vez de bloquear**: dice qué falta y
 *    da el acceso para arreglarlo.
 *
 * La vista por unidades convive con la de jefaturas y se llena sola cuando el tenant crea
 * su primera división; sin divisiones muestra a todos en «sin unidad» y ofrece crearlas.
 */
import { todayISO } from '../../../lib/utils'
import { useMemo, useState } from 'react'
import {
  AlertTriangle, ChevronDown, ChevronRight, Repeat, Search, Settings2,
  UserCog, Users,
} from 'lucide-react'

import { Button } from '@/components/Button'
import DatePicker from '@/components/DatePicker'
import { EmptyState } from '@/components/EmptyState'
import { SegmentGroup } from '@/components/filters'
import { usePermission } from '@/hooks/usePermission'
import { inputCls } from '@/lib/formStyles'

import { AssignmentModal } from './AssignmentModal'
import { OrgUnitsModal } from './OrgUnitsModal'
import {
  useOrgChart,
  type OrgNode, type OrgPerson, type SupervisorChart, type UnitChart,
} from '../hooks/useOrgChart'

type Eje = 'supervisor' | 'unit'
type Objetivo = { id: string; name: string }

const norm = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/** ¿El nodo o alguno de sus descendientes coincide con la búsqueda? */
function contieneCoincidencia(n: OrgNode, q: string): boolean {
  if (!q) return true
  if (norm(`${n.name} ${n.position}`).includes(q)) return true
  return n.children.some(c => contieneCoincidencia(c, q))
}

function Nodo({ nodo, nivel, q, colapsados, alternar, onAsignar, puedeAsignar }: {
  nodo: OrgNode
  nivel: number
  q: string
  colapsados: Set<string>
  alternar: (id: string) => void
  onAsignar: (o: Objetivo) => void
  puedeAsignar: boolean
}) {
  const tieneHijos = nodo.children.length > 0
  // Buscando, las ramas con coincidencias se abren solas: colapsadas esconderían justo
  // lo que se está buscando.
  const abierto = q ? true : !colapsados.has(nodo.id)
  const coincide = !!q && norm(`${nodo.name} ${nodo.position}`).includes(q)
  const hijos = q ? nodo.children.filter(c => contieneCoincidencia(c, q)) : nodo.children

  return (
    <li>
      <div
        className={`group flex items-start gap-2 rounded-lg border px-3 py-2 ${
          coincide
            ? 'border-primary bg-primary/5'
            : nodo.cycle
              ? 'border-danger-border-strong bg-danger-surface'
              : 'border-divider bg-surface'
        }`}
      >
        {tieneHijos ? (
          <button
            type="button"
            onClick={() => alternar(nodo.id)}
            className="mt-0.5 text-content-subtle hover:text-content"
            aria-label={abierto ? 'Colapsar' : 'Expandir'}
          >
            {abierto ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </button>
        ) : (
          <span className="w-[15px]" />
        )}

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-content truncate">
            {nodo.name}
            {nodo.cycle && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs text-danger-content-soft">
                <Repeat size={11} /> jefatura circular
              </span>
            )}
          </p>
          <p className="text-xs text-content-subtle truncate">
            {nodo.position || <span className="italic">sin cargo registrado</span>}
          </p>
        </div>

        {tieneHijos && (
          <div className="shrink-0 text-right">
            <p className="text-xs text-content-muted whitespace-nowrap">
              {nodo.subordinate_count} directo{nodo.subordinate_count === 1 ? '' : 's'}
            </p>
            {nodo.team_size > nodo.subordinate_count && (
              <p className="text-[11px] text-content-subtle whitespace-nowrap">
                {nodo.team_size} en total
              </p>
            )}
          </div>
        )}

        {puedeAsignar && (
          <button
            type="button"
            onClick={() => onAsignar({ id: nodo.id, name: nodo.name })}
            className="shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 text-content-subtle hover:text-primary transition-opacity"
            aria-label={`Asignar jefatura y unidad a ${nodo.name}`}
            title="Asignar jefatura y unidad"
          >
            <UserCog size={15} />
          </button>
        )}
      </div>

      {tieneHijos && abierto && (
        <ul className="ml-4 mt-1.5 space-y-1.5 border-l border-divider pl-3">
          {hijos.map(h => (
            <Nodo key={h.id} nodo={h} nivel={nivel + 1} q={q} colapsados={colapsados}
                  alternar={alternar} onAsignar={onAsignar} puedeAsignar={puedeAsignar} />
          ))}
        </ul>
      )}
    </li>
  )
}

function Personas({ gente, onAsignar, puedeAsignar }: {
  gente: OrgPerson[]
  onAsignar: (o: Objetivo) => void
  puedeAsignar: boolean
}) {
  if (!gente.length) return null
  return (
    <ul className="space-y-1.5">
      {gente.map(p => (
        <li key={p.id}
            className="group flex items-center gap-2 rounded-lg border border-divider bg-surface px-3 py-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-content truncate">{p.name}</p>
            <p className="text-xs text-content-subtle truncate">
              {p.position || <span className="italic">sin cargo registrado</span>}
            </p>
          </div>
          {puedeAsignar && (
            <button type="button" onClick={() => onAsignar({ id: p.id, name: p.name })}
                    className="shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 text-content-subtle hover:text-primary transition-opacity"
                    aria-label={`Asignar jefatura y unidad a ${p.name}`}
                    title="Asignar jefatura y unidad">
              <UserCog size={15} />
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}

export default function OrgChartPage() {
  const { can } = usePermission()
  const puedeAsignar = can('payroll.employees', 'edit')
  const puedeUnidades = can('payroll.org_units', 'view')

  const [eje, setEje] = useState<Eje>('supervisor')
  const [asOf, setAsOf] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [colapsados, setColapsados] = useState<Set<string>>(new Set())
  const [asignando, setAsignando] = useState<Objetivo | null>(null)
  const [verUnidades, setVerUnidades] = useState(false)

  const { data, isLoading, isError } = useOrgChart({ by: eje, as_of: asOf || undefined })
  const q = norm(busqueda.trim())

  const alternar = (id: string) => setColapsados(prev => {
    const s = new Set(prev)
    if (s.has(id)) s.delete(id); else s.add(id)
    return s
  })

  const jefaturas = eje === 'supervisor' ? (data as SupervisorChart | undefined) : undefined
  const unidades = eje === 'unit' ? (data as UnitChart | undefined) : undefined
  const c = data?.completeness

  const raices = useMemo(
    () => (jefaturas?.roots || []).filter(n => contieneCoincidencia(n, q)),
    [jefaturas, q],
  )

  return (
    <div className="space-y-4">
      {/* Controles */}
      <div className="flex flex-wrap items-center gap-2">
        {/* El toggle de la plataforma (`SegmentGroup`), el mismo que usan los filtros
            «Activos / Todos» de clientes y productos. */}
        <SegmentGroup
          options={[{ value: 'supervisor', label: 'Jefaturas' },
                    { value: 'unit', label: 'Unidades' }]}
          value={eje}
          onChange={v => setEje(v as Eje)}
        />

        <div className="relative">
          <Search size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar persona o cargo"
            className={`${inputCls} pl-8 w-56`}
            placeholder="Buscar persona o cargo…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>

        <label className="flex items-center gap-1.5 text-xs text-content-muted">
          Al día
          {/* El DatePicker de la plataforma, no el nativo del navegador: mismo formato
              dd-mm-aaaa, mismos dropdowns de año y mes, y mismo aspecto en claro y
              oscuro que el resto de las pantallas. */}
          <div className="w-44">
            <DatePicker
              value={asOf}
              onChange={setAsOf}
              max={todayISO()}
              aria-label="Ver el organigrama a una fecha pasada"
            />
          </div>
        </label>
        {asOf && (
          <Button variant="secondary" size="sm" onClick={() => setAsOf('')}>Volver a hoy</Button>
        )}

        <div className="flex-1" />

        {puedeUnidades && (
          <Button variant="secondary" size="sm" onClick={() => setVerUnidades(true)}>
            <Settings2 size={14} /> Divisiones y áreas
          </Button>
        )}
      </div>

      {asOf && (
        <p className="text-xs text-content-muted bg-surface-muted border border-divider rounded-lg px-3 py-2">
          Estás viendo la organización <strong>al {asOf}</strong>, reconstruida desde el
          historial de asignaciones. Incluye a quienes ya no trabajan en la empresa, porque
          entonces sí lo hacían.
        </p>
      )}

      {/* Completitud — informa, no bloquea */}
      {c && c.total > 0 && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-content-muted bg-surface border border-divider rounded-lg px-3 py-2">
          <span><strong className="text-content">{c.total}</strong> personas</span>
          <span>{c.with_supervisor} con jefatura</span>
          {c.without_supervisor > 0 && (
            <span className="text-warning">{c.without_supervisor} sin jefatura</span>
          )}
          {c.without_position > 0 && <span>{c.without_position} sin cargo</span>}
          <span>{c.with_unit} con área o división</span>
        </div>
      )}

      {isLoading && <p className="text-sm text-content-muted">Cargando organigrama…</p>}
      {isError && (
        <p className="text-sm text-danger-content-soft">
          No se pudo cargar el organigrama. Vuelve a intentarlo.
        </p>
      )}

      {/* Tenant sin datos: la pantalla abre igual y dice qué hacer */}
      {!isLoading && c?.total === 0 && (
        <EmptyState
          icon={Users}
          title="Todavía no hay trabajadores registrados"
          hint="El organigrama se arma solo a medida que se cargan las fichas y sus jefaturas."
          size="md"
        />
      )}

      {/* ── Eje de jefaturas ── */}
      {jefaturas && c && c.total > 0 && (
        <>
          {jefaturas.in_cycle.length > 0 && (
            <div className="rounded-lg border border-danger-border-strong bg-danger-surface px-3 py-2">
              <p className="text-sm text-danger-content flex items-center gap-1.5">
                <AlertTriangle size={14} />
                {jefaturas.in_cycle.length} persona(s) con jefaturas circulares
              </p>
              <p className="text-xs text-danger-content mt-0.5">
                Dependen entre sí en círculo ({jefaturas.in_cycle.map(p => p.name).join(', ')}),
                así que no tienen un lugar real en la jerarquía. Corrige la jefatura de una de
                ellas para deshacerlo.
              </p>
            </div>
          )}

          {jefaturas.orphans.length > 0 && (
            <div className="rounded-lg border border-divider bg-glass-3 px-3 py-2">
              <p className="text-sm text-warning flex items-center gap-1.5">
                <AlertTriangle size={14} />
                {jefaturas.orphans.length} persona(s) con un jefe que ya no está en la empresa
              </p>
              <p className="text-xs text-content-muted mt-0.5">
                {jefaturas.orphans.map(p => p.name).join(', ')} — aparecen arriba del
                organigrama hasta que se les asigne una jefatura vigente.
              </p>
            </div>
          )}

          {raices.length === 0 ? (
            <EmptyState
              icon={Search}
              title={`Ninguna persona coincide con «${busqueda}»`}
              size="sm"
            />
          ) : (
            <ul className="space-y-2">
              {raices.map(n => (
                <Nodo key={n.id} nodo={n} nivel={0} q={q} colapsados={colapsados}
                      alternar={alternar} onAsignar={setAsignando} puedeAsignar={puedeAsignar} />
              ))}
            </ul>
          )}
        </>
      )}

      {/* ── Eje de unidades ── */}
      {unidades && c && c.total > 0 && (
        <>
          {unidades.divisions.length === 0 && unidades.areas_without_division.length === 0 && (
            <div className="rounded-lg border border-divider bg-glass-3 px-3 py-3">
              <p className="text-sm text-content">
                Esta empresa todavía no tiene divisiones ni áreas.
              </p>
              <p className="text-xs text-content-muted mt-0.5">
                Se pueden crear cuando haga falta; el organigrama por jefaturas funciona sin
                ellas. Mientras tanto, las {unidades.unassigned.length} personas aparecen
                abajo sin unidad.
              </p>
              {puedeUnidades && (
                <Button variant="secondary" size="sm" className="mt-2"
                        onClick={() => setVerUnidades(true)}>
                  <Settings2 size={14} /> Crear divisiones y áreas
                </Button>
              )}
            </div>
          )}

          {unidades.divisions.map(d => (
            <section key={d.id} className="rounded-card border border-divider bg-glass backdrop-blur-glass p-3 shadow-card">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-content">
                  <span className="font-mono text-xs text-content-muted mr-2">{d.code}</span>
                  {d.name}
                </h3>
                <p className="text-xs text-content-subtle">
                  {d.manager ? `Gerente: ${d.manager}` : 'Sin gerente'}
                </p>
              </div>
              <div className="mt-2 space-y-3">
                {d.areas.map(a => (
                  <div key={a.id} className="pl-3 border-l border-divider">
                    <div className="flex items-baseline justify-between gap-2 mb-1.5">
                      <p className="text-sm text-content">
                        <span className="font-mono text-xs text-content-muted mr-2">{a.code}</span>
                        {a.name}
                      </p>
                      <p className="text-xs text-content-subtle">
                        {a.manager ? `Jefe: ${a.manager}` : 'Sin jefe'} · {a.employees.length}
                      </p>
                    </div>
                    {a.employees.length === 0 ? (
                      <p className="text-xs text-content-subtle italic">Sin personas asignadas.</p>
                    ) : (
                      <Personas gente={a.employees} onAsignar={setAsignando}
                                puedeAsignar={puedeAsignar} />
                    )}
                  </div>
                ))}
                {d.employees.length > 0 && (
                  <div className="pl-3 border-l border-divider">
                    <p className="text-xs text-content-muted mb-1.5">
                      En la división, sin área
                    </p>
                    <Personas gente={d.employees} onAsignar={setAsignando}
                              puedeAsignar={puedeAsignar} />
                  </div>
                )}
              </div>
            </section>
          ))}

          {unidades.areas_without_division.map(a => (
            <section key={a.id} className="rounded-card border border-divider bg-glass backdrop-blur-glass p-3 shadow-card">
              <div className="flex items-baseline justify-between gap-2 mb-1.5">
                <h3 className="text-sm font-semibold text-content">
                  <span className="font-mono text-xs text-content-muted mr-2">{a.code}</span>
                  {a.name}
                  <span className="ml-2 text-xs font-normal text-content-subtle">
                    (sin división)
                  </span>
                </h3>
                <p className="text-xs text-content-subtle">
                  {a.manager ? `Jefe: ${a.manager}` : 'Sin jefe'} · {a.employees.length}
                </p>
              </div>
              <Personas gente={a.employees} onAsignar={setAsignando} puedeAsignar={puedeAsignar} />
            </section>
          ))}

          {unidades.unassigned.length > 0 && (
            <section className="rounded-lg border border-divider bg-glass-3 p-3">
              <h3 className="text-sm font-semibold text-content mb-1.5">
                Sin unidad asignada
                <span className="ml-2 text-xs font-normal text-content-subtle">
                  {unidades.unassigned.length}
                </span>
              </h3>
              <Personas gente={unidades.unassigned} onAsignar={setAsignando}
                        puedeAsignar={puedeAsignar} />
            </section>
          )}
        </>
      )}

      {asignando && (
        <AssignmentModal employee={asignando} onClose={() => setAsignando(null)} />
      )}
      {verUnidades && <OrgUnitsModal onClose={() => setVerUnidades(false)} />}
    </div>
  )
}
